import React, { useState, useEffect, useContext } from "react";

import { Card, message, Result, Select, Spin, Table } from "antd";
import "../../App.css";
import TablaEventos from "../../components/modales/eventos/components/TablaEventos";
import PageHeader from "../../components/headers/PageHeader";
import BotonExportarExcelM from "../../components/botones/BotonExportarExcelM";
import ModalNuevoEvento from "../../components/modales/eventos/ModalNuevoEvento";
import ModalReporteCierres from "../../components/modales/eventos/ModalReporteCierres";
import DateRangeFilter from "../../components/modales/eventos/components/DateRangeFilter";
import GestorActividades from "./components/GestorActividades";
import { useUser } from "../../context/UserContext";
import { AuthContext } from "../../context/AuthProvider";
import activitiesService from "../../services/activitiesService";
import dayjs from "dayjs";
import eventService from "../../services/eventService";

/**
 * Contenido de la fila expandida de un evento.
 *
 * Vive fuera de ListaEventos a propósito. Declarado dentro del render, su
 * identidad de componente cambiaba en cada re-render del padre y React
 * desmontaba y remontaba todo el subárbol: el paso del gestor volvía a 1 y el
 * auto-posicionamiento se disparaba otra vez. Por eso el usuario no podía
 * retroceder a revisar o editar una actividad anterior, la aplicación lo
 * devolvía siempre al paso pendiente.
 */
const ExpandedRowContent = ({
  record,
  expandedRowData,
  setExpandedRowData,
  fetchActividades,
  fetchData,
}) => {
  const [currentOrder, setCurrentOrder] = useState(1);

  // Al cambiar de evento se arranca de cero; dentro del mismo evento el paso
  // que elija el usuario se respeta.
  useEffect(() => {
    setCurrentOrder(1);
  }, [record.id]);

  /**
   * Función para manejar la actualización de actividades de forma local
   */
  const handleActivityUpdate = (activity, newState) => {
    try {
      // Actualizar el estado local de la actividad
      const updatedData = expandedRowData.map((item) =>
        item.protocol_activity_subactivity ===
        activity.protocol_activity_subactivity
          ? { ...item, state: newState }
          : item
      );
      setExpandedRowData(updatedData);
    } catch (error) {
      console.error("Error al actualizar actividad:", error);
    }
  };

  // Refresca las actividades de esta fila sin el parpadeo de "cargando"
  // completo (a diferencia de consultaACTIVIDADES). Se usa para detectar
  // sub-actividades que el backend haya generado como consecuencia de una
  // acción, antes de decidir si el protocolo avanza al siguiente paso.
  const refrescarActividades = async () => {
    const items = await fetchActividades(record);
    if (items) {
      setExpandedRowData(items);
    }
    return items;
  };

  if (!Array.isArray(expandedRowData)) return <Spin />;

  if (record.state === "Open" || expandedRowData.length === 0) {
    return (
      <Result
        status={record.state !== "Open" ? "404" : "warning"}
        title={
          record.state !== "Open"
            ? "¡Ups! No logramos cargar las actividades. Intenta refrescar la página o vuelve a iniciar el protocolo."
            : "Aun no has iniciado el protocolo"
        }
      />
    );
  }

  return (
    <GestorActividades
      // El gestor se reinicia solo al cambiar de evento: así el
      // auto-posicionamiento corre una vez por evento y no pisa la navegación
      // manual del usuario.
      key={record.id}
      data={expandedRowData}
      currentOrder={currentOrder}
      onChange={(newOrder) => setCurrentOrder(newOrder)}
      consultaACTIVIDADES={refrescarActividades}
      onActivityUpdate={handleActivityUpdate}
      fetchData={fetchData}
    />
  );
};

const ListaEventos = () => {
  const [loadingTable, setLoadingTable] = useState(true);
  const [eventTable, setEventTable] = useState([]);
  const [, setEventFilter] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [filters] = useState({});
  const [filteredItemCount, setFilteredItemCount] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [appliedDateRange, setAppliedDateRange] = useState([
    dayjs().subtract(15, "days").format("YYYY-MM-DD"),
    dayjs().format("YYYY-MM-DD"),
  ]);
  const [expandedRowData, setExpandedRowData] = useState({});
  const [, setCurrentRecord] = useState(null);
  const [tablePagination, setTablePagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const user = useUser();
  const { UsuariosList, session } = useContext(AuthContext);
  const team = session?.user?.team;
  const [selectedCreatedBy, setSelectedCreatedBy] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);

  const userMap = {};
  (UsuariosList || []).forEach((u) => {
    if (u.id) userMap[u.id] = u.email;
  });

  const displayedEvents = eventTable
    .filter((e) => !selectedCreatedBy || e.createdBy === selectedCreatedBy)
    .filter((e) => !selectedZone || e.location?.zone === selectedZone)
    .filter((e) => !selectedSource || e.source === selectedSource);

  const creatorOptions = [...new Set(eventTable.map((e) => e.createdBy))]
    .filter(Boolean)
    .map((id) => ({ value: id, label: userMap[id] || id }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const zoneOptions = [
    ...new Set(
      eventTable.map((e) => e.location?.zone).filter((z) => z != null)
    ),
  ]
    .sort((a, b) => a - b)
    .map((z) => ({ value: z, label: `Zona ${z}` }));

  const sourceOptions = [...new Set(eventTable.map((e) => e.source).filter(Boolean))]
    .sort()
    .map((s) => ({ value: s, label: s }));

  useEffect(() => {
    const count = eventTable
      .filter((e) => !selectedCreatedBy || e.createdBy === selectedCreatedBy)
      .filter((e) => !selectedZone || e.location?.zone === selectedZone)
      .filter((e) => !selectedSource || e.source === selectedSource).length;
    setFilteredItemCount(count);
    setTablePagination((prev) => ({ ...prev, current: 1 }));
  }, [selectedCreatedBy, selectedZone, selectedSource, eventTable]);

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Título",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Tipo",
      dataIndex: "type",
      key: "type",
      render: (type) => (Array.isArray(type) ? type.join(", ") : type),
    },
    {
      title: "Impacto",
      dataIndex: "impact",
      key: "impact",
    },
    {
      title: "Riesgo",
      dataIndex: "risk",
      key: "risk",
      render: (risk) => (Array.isArray(risk) ? risk.join(", ") : risk),
    },
    {
      title: "Nivel de Riesgo",
      dataIndex: "riskLevel",
      key: "riskLevel",
    },
    {
      title: "Estado",
      dataIndex: "state",
      key: "state",
    },
    {
      title: "Fuente",
      dataIndex: "source",
      key: "source",
    },
    {
      title: "Creado por",
      dataIndex: "createdBy",
      key: "createdBy",
    },
    {
      title: "Fecha de Creación",
      dataIndex: "createdAt",
      key: "createdAt",
    },
    {
      title: "Fecha de Actualización",
      dataIndex: "updatedAt",
      key: "updatedAt",
    },
    {
      title: "Fecha del Evento",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Nombre Ubicación",
      dataIndex: ["location", "name"],
      key: "locationName",
    },
    {
      title: "Código Específico",
      dataIndex: ["location", "specific"],
      key: "locationSpecific",
    },
    {
      title: "Tipo Ubicación",
      dataIndex: ["location", "type"],
      key: "locationType",
    },
    {
      title: "Zona",
      dataIndex: ["location", "zone"],
      key: "locationZone",
    },
    {
      title: "Medio de Transporte",
      dataIndex: ["location", "means"],
      key: "locationMeans",
    },
    {
      title: "Vehículo",
      dataIndex: ["location", "vehicle"],
      key: "locationVehicle",
    },
    {
      title: "Latitud",
      dataIndex: ["location", "geolocation", "lat"],
      key: "locationLat",
    },
    {
      title: "Longitud",
      dataIndex: ["location", "geolocation", "lon"],
      key: "locationLon",
    },
    {
      title: "Causas",
      dataIndex: "causes",
      key: "causes",
      render: (causes) => (Array.isArray(causes) ? causes.join(", ") : causes),
    },
    {
      title: "Protocolos",
      dataIndex: "protocols",
      key: "protocols",
      render: (protocols) =>
        Array.isArray(protocols) ? protocols.join(", ") : protocols,
    },
    {
      title: "Fecha Estado Abierto",
      dataIndex: ["stateHistory", "Open", "date"],
      key: "stateOpenDate",
    },
    {
      title: "Usuario Estado Abierto",
      dataIndex: ["stateHistory", "Open", "userId"],
      key: "stateOpenUser",
    },
    {
      title: "Fecha Estado En Progreso",
      dataIndex: ["stateHistory", "InProgress", "date"],
      key: "stateInProgressDate",
    },
    {
      title: "Usuario Estado En Progreso",
      dataIndex: ["stateHistory", "InProgress", "userId"],
      key: "stateInProgressUser",
    },
    {
      title: "Fecha Estado Cerrado",
      dataIndex: ["stateHistory", "Closed", "date"],
      key: "stateClosedDate",
    },
    {
      title: "Usuario Estado Cerrado",
      dataIndex: ["stateHistory", "Closed", "userId"],
      key: "stateClosedUser",
    },
    {
      title: "Fecha Falsa Alarma",
      dataIndex: ["stateHistory", "FalseAlarm", "date"],
      key: "stateFalseAlarmDate",
    },
    {
      title: "Usuario Falsa Alarma",
      dataIndex: ["stateHistory", "FalseAlarm", "userId"],
      key: "stateFalseAlarmUser",
    },
    {
      title: "Reporte de Costo",
      dataIndex: ["cost", "report"],
      key: "costReport",
    },
    {
      title: "Descripción de Costo",
      dataIndex: ["cost", "description"],
      key: "costDescription",
    },
    {
      title: "Costo Total",
      dataIndex: ["cost", "total"],
      key: "costTotal",
    },
    {
      title: "Versión",
      dataIndex: "_version",
      key: "_version",
    },
    {
      title: "Último Cambio",
      dataIndex: "_lastChangedAt",
      key: "_lastChangedAt",
    },
    {
      title: "Tipo de Entidad",
      dataIndex: "__typename",
      key: "__typename",
    },
  ];

  const onSelectChange = (newSelectedRowKeys, selectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setDocumentos(selectedRows);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
  };

  const handleTableChange = (pagination, filters, sorter, extra) => {
    const currentLength =
      extra?.currentDataSource?.length ?? eventTable.length ?? 0;
    setTablePagination((prev) => ({
      ...prev,
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: currentLength,
    }));
    setFilteredItemCount(currentLength);
  };

  const AjusteArray = (arrayEvents) => {
    const Events = arrayEvents
      .filter(
        (item) =>
          item.id &&
          typeof item.id === "string" &&
          item.id.match(/^(CISE|EMER)-\d{5}-\d{4}$/)
      )
      .sort((a, b) => {
        const idA = parseInt(a.id.split("-")[1]); // Obtener el número del campo 'id' para el objeto 'a'
        const idB = parseInt(b.id.split("-")[1]); // Obtener el número del campo 'id' para el objeto 'b'
        return idB - idA; // Ordenar de mayor a menor
      });

    setEventTable(Events);
    setEventFilter(Events);
    setFilteredItemCount(Events.length || 0);
    setTablePagination((prev) => ({
      ...prev,
      current: 1,
      total: Events.length || 0,
    }));
  };

  const obtenerEventos = async (startDate = null, endDate = null) => {
    setLoadingTable(true);
    const token = user?.idToken || "";

    if (!token) {
      setLoadingTable(false);
      return;
    }

    let allEventos = [];
    let startKey = null;

    try {
      do {
        const data = await eventService.consultarEventos(
          startDate,
          endDate,
          startKey,
          token,
          team
        );

        const eventosFiltrados = (
          data?.Items?.filter((evento) =>
            /^(CISE|EMER)-/.test(evento.id || "")
          ) || []
        ).sort((a, b) => (a.id < b.id ? 1 : -1));

        allEventos = allEventos.concat(eventosFiltrados);
        startKey = data?.LastEvaluatedKey || null;
      } while (startKey);
    } catch (err) {
      console.error("Error al consultar eventos:", err);
    } finally {
      setLoadingTable(false);
      setEventTable(allEventos);

      setEventFilter(allEventos);
      setFilteredItemCount(allEventos.length || 0);
      setTablePagination((prev) => ({
        ...prev,
        current: 1,
        total: allEventos.length || 0,
      }));
    }
  };

  useEffect(() => {
    // Este código se ejecutará solo cuando `open` sea `true`
    setLoadingTable(true);
    setEventTable([]);
    const endDate = dayjs().add(1, "days").format("YYYY-MM-DD"); // Fecha actual en formato "YYYY-MM-DD"
    const startDate = dayjs().subtract(15, "days").format("YYYY-MM-DD");
    obtenerEventos(startDate, endDate);

    // fetchData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExpand = async (expanded, record) => {
    if (expanded) {
      // Ejecutar la función asincrónica al expandir la fila
      const result = await consultaACTIVIDADES(record);
      setExpandedRowData(result);
      setCurrentRecord(record);
    }
  };

  // Consulta las actividades sin tocar el estado (usada tanto por
  // consultaACTIVIDADES como por refrescos silenciosos dentro de una fila ya
  // expandida, para no forzar el spinner de carga completo).
  const fetchActividades = async (record) => {
    try {
      const response = await activitiesService.consultarActividadEspecifica(
        record.id,
        record.protocols[0],
        user?.idToken || ""
      );

      return response?.Items || null;
    } catch (error) {
      console.error("Error fetching data:", error);
      return null;
    }
  };

  const consultaACTIVIDADES = async (record) => {
    setExpandedRowData(null);

    const items = await fetchActividades(record);
    if (!items) {
      message.error("No se logró hacer la consulta");
    }
    return items;
  };

  return (
    <Card
      title={
        <PageHeader
          title="Registro de Eventos"
          head="Eventos"
          help={true}
          mensaje="Esta vista incorpora un registro detallado de todos los eventos creados en la plataforma durante los últimos 15 días.

            Si deseas consultar eventos anteriores a ese rango, puedes utilizar el filtro de fecha ubicado en la parte superior.
            Adicionalmente, el botón “+” despliega el Gestor de Protocolos, desde donde podrás gestionar tu evento de forma completa, sin necesidad de cambiar de pantalla."
        ></PageHeader>
      }
      extra={
        <>
          <BotonExportarExcelM documentos={documentos} columns={columns} />
          <ModalReporteCierres eventTable={eventTable} dateRange={appliedDateRange} />
          <ModalNuevoEvento fetchData={obtenerEventos} />
        </>
      }
    >
      <div>
        <DateRangeFilter
          type="eventos"
          filteredItemCount={filteredItemCount}
          loadingButton={loadingTable}
          setLoadingButton={setLoadingTable}
          AjusteArray={AjusteArray}
          onRangeChange={setAppliedDateRange}
        />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "8px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: "5px" }}>Filtrar por usuario:</span>
          <Select
            allowClear
            showSearch
            placeholder="Todos los usuarios"
            style={{ width: 280 }}
            value={selectedCreatedBy}
            onChange={(val) => setSelectedCreatedBy(val ?? null)}
            options={creatorOptions}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: "5px" }}>Filtrar por zona:</span>
          <Select
            allowClear
            placeholder="Todas las zonas"
            style={{ width: 150 }}
            value={selectedZone}
            onChange={(val) => setSelectedZone(val ?? null)}
            options={zoneOptions}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: "5px" }}>Filtrar por fuente:</span>
          <Select
            allowClear
            showSearch
            placeholder="Todas las fuentes"
            style={{ width: 200 }}
            value={selectedSource}
            onChange={(val) => setSelectedSource(val ?? null)}
            options={sourceOptions}
          />
        </div>
      </div>
      <Table
        className="textAjust"
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        rowKey="id"
        style={{ marginTop: "10px" }}
        loading={loadingTable}
        responsive={{
          xs: "scroll",
          sm: "scroll",
          md: "scroll",
          lg: "scroll",
        }}
        dataSource={displayedEvents}
        columns={TablaEventos(eventTable, obtenerEventos)}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
        expandable={{
          expandedRowRender: (record) => (
            <ExpandedRowContent
              record={record}
              expandedRowData={expandedRowData}
              setExpandedRowData={setExpandedRowData}
              fetchActividades={fetchActividades}
              fetchData={obtenerEventos}
            />
          ),
          onExpand: handleExpand,
          rowExpandable: (record) => record.state !== "Created",
        }}
        {...(Object.keys(filters).length > 0 ? { filters: filters } : {})}
        locale={{
          filterTitle: "Filtrar",
          filterConfirm: "Aceptar",
          filterReset: "Limpiar",
          emptyText: "Sin datos",
        }}
        pagination={{
          ...tablePagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} registros`,
        }}
      />
    </Card>
  );
};

export default ListaEventos;
